import React from 'react';
import { Link } from 'react-router-dom';

export default function BunkerPrivacyPolicy() {
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800/50 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-lg font-mono text-zinc-400 hover:text-white transition-colors">
            ← Back to Portfolio
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-zinc-400 text-lg">Bunker - Whiskey Collection & Tasting Journal</p>
            <p className="text-zinc-500 text-sm mt-2">Last updated: July 20, 2026</p>
          </div>

          <div className="space-y-10 text-zinc-300 leading-relaxed">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Introduction</h2>
              <p>
                Welcome to Bunker (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;). Bunker is a local-first app for
                cataloging your whiskey collection, logging pours and tasting notes, and deciding
                what to pour tonight. This Privacy Policy explains how information is handled when
                you use our mobile application (&quot;App&quot;). The short version: your bunker lives on
                your device, and anything beyond that is strictly opt-in.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Information We Collect</h2>
              <p className="mb-4">
                <strong className="text-white">
                  By default, we collect no personal information at all.
                </strong>{' '}
                Without an account:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>We do not collect your name, email address, or any contact information</li>
                <li>We do not collect payment information (subscriptions are handled entirely by Apple)</li>
                <li>We do not track your location</li>
                <li>We do not use cookies or similar tracking technologies</li>
                <li>We do not collect device identifiers or usage analytics</li>
              </ul>
              <p className="mt-4">
                If you choose to create an optional account (see below), we collect your email
                address and a password solely to operate that account.
              </p>
            </section>

            {/* Your Data Stays on Your Device */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Your Data Stays on Your Device</h2>
              <p>
                Everything you create in Bunker — your bottle collection, prices paid, estimated
                values, pour history, tasting notes, hunt list, and any photos you attach — is
                stored locally on your device in the App&apos;s private database. The App works fully
                offline. If you delete the App, this local data is removed from your device, so we
                recommend using the built-in JSON export (Settings → Your data) to keep your own
                backup.
              </p>
            </section>

            {/* Optional Account & Cloud Features */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Optional Account &amp; Cloud Features</h2>
              <p className="mb-4">
                Some premium features (Friends and Cloud Backup) require an optional account,
                created with an email address and password. Account data is stored with our cloud
                database provider, Supabase, and consists of:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Your email address and a securely hashed password (used only for sign-in)</li>
                <li>Your chosen handle (username), if you claim one</li>
                <li>Your friend connections (requests and accepted friendships)</li>
                <li>
                  If — and only if — you turn sharing on: a snapshot of your bar and/or hunt list
                  that accepted friends can view. This snapshot contains bottle names, types,
                  proofs, ratings, fill levels, and flavor tags.{' '}
                  <strong className="text-white">
                    It never contains what you paid, your estimated values, store names, hunt
                    stories, or photos.
                  </strong>
                </li>
                <li>
                  If you use Cloud Backup: a complete private copy of your bunker data (including
                  prices paid and estimated values), readable only by your account and used solely
                  to restore your data
                </li>
              </ul>
              <p className="mt-4">
                Sharing is off by default. Turning it off again deletes the shared snapshot from
                our servers. Only friends you have explicitly accepted can ever see shared data —
                there is no public directory, feed, or search of collections.
              </p>
            </section>

            {/* Account Deletion */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Account Deletion</h2>
              <p>
                You can permanently delete your account at any time in the App (Settings → Account
                → Delete account). This removes your account, profile, friendships, shared
                snapshots, and cloud backup from our servers. The local data on your device is
                unaffected and remains yours.
              </p>
            </section>

            {/* Subscriptions */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Subscriptions</h2>
              <p>
                Bunker Premium is an auto-renewable subscription purchased through Apple. Your
                payment details are handled entirely by Apple — we never see them. We use
                RevenueCat, a subscription-management service, to validate purchases; it processes
                an anonymized app-user identifier and purchase receipt data, not your identity.
              </p>
            </section>

            {/* Camera & Barcode Scanning */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Camera &amp; Barcode Scanning</h2>
              <p className="mb-4">
                Bunker uses your device&apos;s camera to scan the barcodes on bottles. The camera feed
                is processed entirely on your device — no photos or video are ever recorded,
                stored, or uploaded by the scanning feature.
              </p>
              <p>
                When a scanned barcode isn&apos;t already recognized by the App, the numeric barcode
                digits (and nothing else) are sent to third-party product databases to identify
                the bottle. This request contains no personal information and is not linked to you.
              </p>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Third-Party Services</h2>
              <p className="mb-4">
                To identify an unrecognized barcode, the App may query the following public
                product databases with the barcode digits only:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <a
                    href="https://www.upcitemdb.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-400 hover:text-amber-300 underline"
                  >
                    UPCitemdb
                  </a>
                </li>
                <li>
                  <a
                    href="https://world.openproductsfacts.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-400 hover:text-amber-300 underline"
                  >
                    Open Products Facts
                  </a>
                </li>
              </ul>
              <p className="mt-4">
                The optional account and cloud features described above are operated on Supabase,
                and subscription validation is provided by RevenueCat. No other third parties
                receive any data from the App.
              </p>
            </section>

            {/* Photos */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Photos</h2>
              <p>
                If you choose to attach a photo to a bottle, the App asks for access to your
                camera or photo library. The selected photo is resized and copied into the App&apos;s
                local storage on your device only. Photos are never uploaded anywhere — not even
                to friends or cloud backups.
              </p>
            </section>

            {/* Data Export */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Data Export</h2>
              <p>
                You can export all of your Bunker data as a JSON file at any time from Settings.
                Exports are created on your device and shared only where you choose to send them
                (e.g., your own cloud storage or email). Once you share an export, its privacy is
                governed by the service you shared it to.
              </p>
            </section>

            {/* Age */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Age Requirement</h2>
              <p>
                Bunker is a journal about whiskey and is intended solely for users of legal
                drinking age in their jurisdiction. The App is rated for adults on the App Store.
                We do not knowingly collect personal information from anyone under the legal
                drinking age, or from children.
              </p>
            </section>

            {/* Changes to This Policy */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. If we make changes, we will
                notify you by revising the &quot;Last updated&quot; date at the top of this policy. We
                encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy or our privacy practices,
                please contact us at:
              </p>
              <p className="mt-4">
                <a
                  href="mailto:parkerdevwork@gmail.com"
                  className="text-amber-400 hover:text-amber-300 underline"
                >
                  parkerdevwork@gmail.com
                </a>
              </p>
            </section>

            {/* Summary */}
            <section className="bg-zinc-900/50 border border-zinc-800/50 rounded-lg p-6 mt-12">
              <h2 className="text-xl font-semibold text-white mb-4">Summary</h2>
              <p className="text-zinc-400">
                In short: your bunker lives on your phone. No account is required, and without one
                we collect nothing. If you opt into an account, we store only what friends and
                backups need. Friends can never see what you paid — that is excluded from sharing
                entirely. If you turn on Cloud Backup, a complete private copy of your bunker
                (including prices) is stored, readable only by your account. Beyond what you
                explicitly opt into, the only thing that ever leaves your phone is the digits of a
                barcode we&apos;re trying to identify.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-zinc-800/50">
        <div className="max-w-4xl mx-auto text-center text-sm text-zinc-600">
          <p>© 2026 Bunker. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
